import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiService, User } from '@/lib/api'
import DashboardLayout from '@/components/DashboardLayout'
import { Suspense } from 'react'
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileImage
} from 'lucide-react'

interface SuggestedAccount {
  id: number
  account_code: string
  account_name: string
  account_type: string
}

interface Classification {
  confidence?: number
  document_type?: string
  vendor_type?: string
  requires_review?: boolean
  review_reason?: string | null
  error?: string
  primary_account?: {
    account_code: string
    account_name: string
    reasoning: string
  }
  suggested_description?: string
}

interface ExtractedData {
  currency?: string
  total_amount?: number
  document_date?: string
  document_number?: string
  vendor_or_customer?: string
  line_items?: Array<{
    description: string
    amount: number
    quantity?: number
    unit_price?: number
  }>
}

interface FileUpload {
  id: number
  chat_session_id?: number
  instruction: string
  status: string
  files_count: number
  requires_review: boolean
  ai_suggestion_ready?: boolean
  can_create_bill?: boolean
  confidence?: number
  created_at: string
  updated_at: string
  user: {
    id: number
    email: string
    full_name: string
  }
  classification?: Classification
  suggested_account?: SuggestedAccount
  extracted_data?: ExtractedData
  created_bill?: {
    id: number
    bill_number: string
    total_amount: number
    status: string
    has_transaction: boolean
  }
}

interface FileUploadsResponse {
  success: boolean
  data: FileUpload[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getStatusBadge(upload: FileUpload) {
  if (upload.created_bill) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
        <CheckCircle size={12} />
        Bill Created
      </span>
    )
  }
  if (upload.status === 'completed' && upload.classification && !upload.classification.error && !upload.requires_review) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
        <CheckCircle size={12} />
        Ready for Review
      </span>
    )
  }
  if (upload.requires_review || upload.classification?.error) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <AlertCircle size={12} />
        Needs Review
      </span>
    )
  }
  if (upload.status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <Loader2 size={12} className="animate-spin" />
        Processing
      </span>
    )
  }
  if (upload.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
        <AlertCircle size={12} />
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20">
      <Clock size={12} />
      {upload.status}
    </span>
  )
}

function DocumentHistoryContent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [user, setUser] = useState<User | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 20

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }, [])

  useEffect(() => {
    const page = searchParams.get('page')
    if (page) {
      setCurrentPage(parseInt(page, 10))
    }
  }, [searchParams])

  const { data, isLoading, error, refetch } = useQuery<FileUploadsResponse>({
    queryKey: ['file-uploads', currentPage, perPage],
    queryFn: () => apiService.getFileUploads({ page: currentPage, per_page: perPage }) as Promise<FileUploadsResponse>,
    enabled: !!user,
  })

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    navigate('/')
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    navigate(`/documents?page=${page}`)
  }

  const handleViewDocument = (upload: FileUpload) => {
    const sessionId = upload.chat_session_id || upload.id
    navigate(`/chat/${sessionId}`)
  }

  const pagination = data?.pagination
  const fileUploads = data?.data || []

  return (
    <DashboardLayout user={user} onLogout={handleLogout}>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="text-cyan-400" size={28} />
            Document History
          </h1>
          <p className="text-slate-400 text-sm sm:text-base">
            View and manage all your uploaded accounting documents
          </p>
        </div>

        <div className="bg-[#0D111D]/80 backdrop-blur-xl rounded-2xl border border-slate-800/50 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <span className="ml-3 text-slate-400">Loading documents...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-slate-300 font-medium">Failed to load documents</p>
              <p className="text-slate-500 text-sm mt-1">Please try again later</p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-600/30 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : fileUploads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <FileImage className="w-16 h-16 text-slate-600 mb-4" />
              <p className="text-slate-300 font-medium text-lg">No documents yet</p>
              <p className="text-slate-500 text-sm mt-1">
                Upload your first document from the Dashboard
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-4 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-500 text-white rounded-xl font-medium hover:from-cyan-600 hover:to-indigo-600 transition-all shadow-lg shadow-cyan-500/25"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/50">
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Document</th>
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Status</th>
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Account</th>
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Confidence</th>
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Uploaded</th>
                      <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/30">
                    {fileUploads.map((upload) => {
                      const confidence = upload.classification?.confidence
                      const description = upload.classification?.suggested_description || upload.extracted_data?.vendor_or_customer || upload.instruction
                      const amount = upload.extracted_data?.total_amount
                      const currency = upload.extracted_data?.currency || 'MYR'
                      const documentType = upload.classification?.document_type

                      return (
                        <tr key={upload.id} className="hover:bg-slate-800/20 transition-colors group cursor-pointer" onClick={() => handleViewDocument(upload)}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-slate-800/50 flex items-center justify-center border border-slate-700/30">
                                <FileText className="w-5 h-5 text-slate-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white truncate max-w-[200px] sm:max-w-[280px]">{description}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {documentType && <span className="text-xs text-slate-500 capitalize">{documentType.replace(/_/g, ' ')}</span>}
                                  {amount && <span className="text-xs text-cyan-400 font-medium">{currency} {amount.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">{getStatusBadge(upload)}</td>
                          <td className="px-6 py-4">
                            {upload.suggested_account ? (
                              <div>
                                <p className="text-xs font-medium text-white">{upload.suggested_account.account_code}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[120px]">{upload.suggested_account.account_name}</p>
                              </div>
                            ) : <span className="text-xs text-slate-500">—</span>}
                          </td>
                          <td className="px-6 py-4">
                            {confidence !== undefined ? (
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${confidence >= 0.9 ? 'bg-green-500' : confidence >= 0.7 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${confidence * 100}%` }} />
                                </div>
                                <span className="text-xs text-slate-400">{(confidence * 100).toFixed(0)}%</span>
                              </div>
                            ) : <span className="text-xs text-slate-500">—</span>}
                          </td>
                          <td className="px-6 py-4"><span className="text-sm text-slate-400">{formatDate(upload.created_at)}</span></td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={(e) => { e.stopPropagation(); handleViewDocument(upload) }} className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors" title="View Details">
                                <Eye size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800/50">
                  <p className="text-sm text-slate-400">Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, pagination.total_count)} of {pagination.total_count} documents</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronLeft size={20} />
                    </button>
                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      let pageNum: number
                      if (pagination.total_pages <= 5) pageNum = i + 1
                      else if (currentPage <= 3) pageNum = i + 1
                      else if (currentPage >= pagination.total_pages - 2) pageNum = pagination.total_pages - 4 + i
                      else pageNum = currentPage - 2 + i
                      return (
                        <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}>
                          {pageNum}
                        </button>
                      )
                    })}
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.total_pages} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {pagination && pagination.total_count > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#0D111D]/80 backdrop-blur-xl rounded-xl border border-slate-800/50 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total Documents</p>
              <p className="text-2xl font-bold text-white">{pagination.total_count}</p>
            </div>
            <div className="bg-[#0D111D]/80 backdrop-blur-xl rounded-xl border border-slate-800/50 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Ready for Review</p>
              <p className="text-2xl font-bold text-cyan-400">{fileUploads.filter(u => u.ai_suggestion_ready && u.can_create_bill && !u.created_bill).length}</p>
            </div>
            <div className="bg-[#0D111D]/80 backdrop-blur-xl rounded-xl border border-slate-800/50 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Bills Created</p>
              <p className="text-2xl font-bold text-green-400">{fileUploads.filter(u => u.created_bill).length}</p>
            </div>
            <div className="bg-[#0D111D]/80 backdrop-blur-xl rounded-xl border border-slate-800/50 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Processing</p>
              <p className="text-2xl font-bold text-amber-400">{fileUploads.filter(u => u.status === 'processing').length}</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}

export default function DocumentHistoryPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    }>
      <DocumentHistoryContent />
    </Suspense>
  )
}
